/**
 * Carbon-style event history (DESIGN.md "Data table"): 8px square events
 * joined by a 1px border-strong line, the label in primary text and a
 * tabular helper timestamp at the end. The current event (the drift itself)
 * fills with the strong neutral `--inverse`, never Soft Green (D18); the
 * others are a 1px ring.
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'

export interface TimelineItem {
  id?: string
  label: ReactNode
  time: ReactNode
  /** A machine-readable time for the <time> element. */
  dateTime?: string
  current?: boolean
}

export interface TimelineProps {
  items: TimelineItem[]
  className?: string
}

export function Timeline({ items, className }: TimelineProps) {
  return (
    <ol className={clsx('m-0 list-none p-0', className)}>
      {items.map((item, i) => (
        <li
          key={item.id ?? i}
          aria-current={item.current ? 'true' : undefined}
          className="relative grid grid-cols-[8px_minmax(0,1fr)_auto] gap-3 pb-4"
        >
          {i < items.length - 1 ? <span aria-hidden="true" className="absolute bottom-0.5 left-[3.5px] top-3.5 w-px bg-line-strong" /> : null}
          <span aria-hidden="true" className={clsx('mt-1.5 h-2 w-2', item.current ? 'bg-inverse' : 'ring-1 ring-inset ring-line-strong')} />
          <span className="min-w-0 text-body text-text-primary">{item.label}</span>
          <time dateTime={item.dateTime} className="whitespace-nowrap pt-0.5 text-helper tabular-nums text-text-helper">
            {item.time}
          </time>
        </li>
      ))}
    </ol>
  )
}

export default Timeline
